// Relative imports (not `@/`) so bun can test this module.
import type { RuleSet } from '../rules';
import { type PlayerId, type SimState, scoreBreakdown } from '../sim';

/**
 * The viewer's own score as it stands, by the end screen's formula (sim
 * victory.ts `scoreBreakdown`): what a timeout would score them now. Own data
 * only (their stock, buildings and army), so it's fog-safe. Pure, no React.
 */
export interface LiveScore {
  /** Stock in their cities, already × `stockWeight`. */
  stock: number;
  /** Invested in standing buildings and their levels (city levels × `cityLevelWeight`). */
  buildings: number;
  /** Training cost of their military units (0 when the rules don't count the army). */
  army: number;
  total: number;
  /** What a resource held counts for (`match.timeout.stockWeight`): spending scores more. */
  stockWeight: number;
  /** What a city level's cost counts for (`match.timeout.cityLevelWeight`). */
  cityLevelWeight: number;
  /** Whether the army counts (`match.timeout.army`). */
  armyCounts: boolean;
}

/**
 * `playerId`'s live score. Pass the real state, not the viewer's
 * (`viewerState`), so a rival spy disguised as one of their units doesn't count.
 */
export function liveScore(state: SimState, rules: RuleSet, playerId: PlayerId): LiveScore {
  const { stockWeight, cityLevelWeight, army } = rules.match.timeout;
  return {
    ...scoreBreakdown(state, rules, playerId),
    stockWeight,
    cityLevelWeight,
    armyCounts: !!army,
  };
}

/** The pill's tooltip: "Score 1234: stock 200 (×0.5) · buildings 900 (city levels ×1.5) · army 134". */
export function liveScoreTitle(score: LiveScore): string {
  const r = Math.round;
  const parts = [
    `stock ${r(score.stock)} (×${score.stockWeight})`,
    `buildings ${r(score.buildings)} (city levels ×${score.cityLevelWeight})`,
  ];
  if (score.armyCounts) parts.push(`army ${r(score.army)}`);
  return `Score ${r(score.total)}: ${parts.join(' · ')}`;
}
